import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { HoldsOverlay } from '../HoldsOverlay';
import { RouteLineOverlay } from '../RouteLineOverlay';
import type { Hold, RouteLine } from '../../../types/climb';
import { WHOLE_PHOTO, cropHolds, cropLine, type CropRect } from './crop';
import type { ClimbPhoto } from './useClimbPhoto';

/**
 * How tall a preview may be drawn.
 *
 * Spent as a **max-width** worked back through the picture's own proportions, the
 * way the editor's own frame spends its cap (see `photo-height.ts`): every mark is
 * a percentage of the box it is drawn in, so the box has to stay exactly the
 * picture's box or a circle would land beside the hold it means. A capped preview
 * therefore narrows and centres itself rather than losing its shape.
 */
const PREVIEW_HEIGHT = { sideBySide: '45dvh', stacked: '28dvh' };

interface CropChoiceDialogProps {
  /** The picture as it stands — the crop is a part of *this*. */
  photo: ClimbPhoto | null;
  /** What was on screen when save was pressed; null when nothing is being asked. */
  crop: CropRect | null;
  holds: Hold[];
  line: RouteLine | null;
  /** The framing to save with: what is on screen, or the whole picture. */
  onChoose: (rect: CropRect) => void;
  onDismiss: () => void;
}

/**
 * Zoomed in at the moment of saving: is the framing the climb, or was it just a
 * closer look?
 *
 * The question is asked with both answers **shown**, drawing and all. A setter
 * who zoomed to place a hold precisely and one who framed the climb made the same
 * gesture, and neither of them is looking at what the words "crop" and "whole
 * photo" would produce — what settles it is seeing the two pictures side by side,
 * with the holds that survive the cut on one of them.
 *
 * The cut shown is the one the save would make: the same `cropHolds`/`cropLine`
 * the save runs, so a circle that falls outside the frame is already gone from
 * the preview rather than turning up missing afterwards.
 */
export function CropChoiceDialog({
  photo,
  crop,
  holds,
  line,
  onChoose,
  onDismiss,
}: CropChoiceDialogProps) {
  const { t } = useTranslation();

  // The part of the photo on screen, in its own pixels, with the drawing moved
  // into it.
  const cut = useMemo(() => {
    if (!photo || !crop) return null;

    const frame = {
      x: crop.left * photo.naturalWidth,
      y: crop.top * photo.naturalHeight,
      width: crop.width * photo.naturalWidth,
      height: crop.height * photo.naturalHeight,
    };

    return {
      frame,
      holds: cropHolds(holds, crop, frame.width, frame.height),
      line: cropLine(
        line,
        crop,
        photo.naturalWidth,
        photo.naturalHeight,
        frame.width,
        frame.height
      ),
    };
  }, [photo, crop, holds, line]);

  // A photo taken at the wall is portrait, and two of those one above the other
  // make a column twice the height of the screen — so portraits stand side by
  // side and landscapes, wide and short, are stacked. One reading serves both
  // pictures: the crop is read off the frame, and the frame keeps the photo's own
  // box, so the two always have the same shape.
  const sideBySide = !!photo && photo.naturalHeight >= photo.naturalWidth;
  const maxHeight = sideBySide ? PREVIEW_HEIGHT.sideBySide : PREVIEW_HEIGHT.stacked;

  return (
    <Dialog open={!!(photo && crop && cut)} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('climbEditor.crop.title')}</DialogTitle>
          <DialogDescription>{t('climbEditor.crop.intro')}</DialogDescription>
        </DialogHeader>

        {photo && crop && cut && (
          <div
            className={cn('grid gap-3', sideBySide ? 'grid-cols-2' : 'grid-cols-1')}
            data-testid="crop-choices"
            data-layout={sideBySide ? 'side-by-side' : 'stacked'}
          >
            <CropChoice
              label={t('climbEditor.crop.crop')}
              preferred
              onClick={() => onChoose(crop)}
              testId="crop-choice-crop"
            >
              <PhotoPreview
                url={photo.url}
                photoWidth={photo.naturalWidth}
                photoHeight={photo.naturalHeight}
                frame={cut.frame}
                holds={cut.holds}
                line={cut.line}
                maxHeight={maxHeight}
              />
            </CropChoice>

            <CropChoice
              label={t('climbEditor.crop.whole')}
              onClick={() => onChoose(WHOLE_PHOTO)}
              testId="crop-choice-whole"
            >
              <PhotoPreview
                url={photo.url}
                photoWidth={photo.naturalWidth}
                photoHeight={photo.naturalHeight}
                frame={{ x: 0, y: 0, width: photo.naturalWidth, height: photo.naturalHeight }}
                holds={holds}
                line={line}
                maxHeight={maxHeight}
              />
            </CropChoice>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** One of the two answers: the picture it would save, with what it is called. */
function CropChoice({
  label,
  preferred,
  onClick,
  testId,
  children,
}: {
  label: string;
  preferred?: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex min-w-0 flex-col items-center gap-2 rounded-lg border p-2 transition-colors hover:bg-accent',
        preferred && 'border-primary'
      )}
    >
      {children}
      <span className="text-center text-sm font-medium">{label}</span>
    </button>
  );
}

/**
 * A part of the photo — or all of it — with the climb drawn on top.
 *
 * The picture is an `<svg>` rather than an `<img>`: its viewBox is the part being
 * shown, in the photo's own pixels, so a crop is *framed* here instead of being
 * cut, and nothing has to be re-encoded to be looked at. The overlays beside it
 * fit by the same rule (`meet`) against the shown part's own size, so every mark
 * lands where it will land on the saved photo.
 */
function PhotoPreview({
  url,
  photoWidth,
  photoHeight,
  frame,
  holds,
  line,
  maxHeight,
}: {
  url: string;
  photoWidth: number;
  photoHeight: number;
  /** The part of the photo on show, in the photo's own pixels. */
  frame: { x: number; y: number; width: number; height: number };
  /** The drawing, in fractions of that part. */
  holds: Hold[];
  line: RouteLine | null;
  maxHeight: string;
}) {
  return (
    <div
      className="relative mx-auto w-full overflow-hidden rounded-md bg-muted"
      style={{
        aspectRatio: `${frame.width} / ${frame.height}`,
        maxWidth: `calc(${maxHeight} * ${frame.width / frame.height})`,
      }}
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <image href={url} x={0} y={0} width={photoWidth} height={photoHeight} />
      </svg>

      {/* Still, both of them: two pictures that draw themselves on opening turn a
          question about framing into something to sit through. */}
      <HoldsOverlay
        holds={holds}
        naturalWidth={frame.width}
        naturalHeight={frame.height}
        objectFit="contain"
        animate={false}
      />
      <RouteLineOverlay
        line={line}
        naturalWidth={frame.width}
        naturalHeight={frame.height}
        objectFit="contain"
        // The corridor drains the colour from the rest of the wall, which at this
        // size would grey out most of what is being judged: how much wall is kept.
        focusOutside={false}
        animate={false}
        marks="compact"
      />
    </div>
  );
}
